import { Add } from "@mui/icons-material";
import { Box, Collapse, Fab, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useState } from "react";

type SectionCardProps = {
	title: string;
	action?: ReactNode;
	filters?: ReactNode;
	children: ReactNode;
	expandable?: boolean;
	defaultExpanded?: boolean;
	separateContent?: boolean;
};

export const SectionCard = ({
	title,
	action,
	filters,
	children,
	expandable = false,
	defaultExpanded = true,
	separateContent = false,
}: SectionCardProps) => {
	const [expanded, setExpanded] = useState(defaultExpanded);

	const handleToggle = () => {
		setExpanded(!expanded);
	};

	const hasFilters = Boolean(filters);

	const headerContent = (
		<Paper
			elevation={0}
			sx={{
				backgroundColor: "#FFFFFF",
				overflow: "visible",
				position: "relative",
				padding: 0,
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{ px: 2, py: 2 }}
			>
				<Typography variant="h2" sx={{ fontWeight: 600, fontSize: 18 }}>
					{title}
				</Typography>
				{!hasFilters && action && (
					<Stack direction="row" alignItems="center" spacing={1}>
						{action}
					</Stack>
				)}
			</Stack>
			{hasFilters && (
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{
						px: 2,
						pb: 2,
					}}
				>
					<Box>{filters}</Box>
					{action && (
						<Stack direction="row" alignItems="center" spacing={1}>
							{action}
						</Stack>
					)}
				</Stack>
			)}
			{expandable && (
				<Box
					sx={{
						position: "absolute",
						bottom: 0,
						left: "50%",
						transform: "translate(-50%, 50%)",
						zIndex: 1,
					}}
				>
					<Fab
						size="small"
						onClick={handleToggle}
						aria-label={expanded ? "Sbalit sekci" : "Rozbalit sekci"}
						sx={{
							width: 32,
							height: 32,
							minHeight: 32,
							backgroundColor: "#535862",
							color: "#FFFFFF",
							boxShadow: "none",
							"&:hover": {
								backgroundColor: "#414651",
							},
						}}
					>
						<Add
							sx={{
								fontSize: 20,
								transform: expanded ? "rotate(45deg)" : "rotate(0deg)",
								transition: "transform 0.2s ease-in-out",
							}}
						/>
					</Fab>
				</Box>
			)}
		</Paper>
	);

	const contentSection = (
		<Collapse in={expanded}>
			<Box
				sx={{
					p: 2,
					backgroundColor: separateContent ? "transparent" : "#FFFFFF",
					border: separateContent ? "none" : "1px solid #E9EAEB",
					borderTop: "none",
					borderBottomLeftRadius: 8,
					borderBottomRightRadius: 8,
				}}
			>
				{children}
			</Box>
		</Collapse>
	);

	if (separateContent) {
		return (
			<Stack spacing={2}>
				{headerContent}
				<Collapse in={expanded}>
					<Box>{children}</Box>
				</Collapse>
			</Stack>
		);
	}

	return (
		<Box>
			{headerContent}
			{contentSection}
		</Box>
	);
};
